/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { useLocation, useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import { Calendar as CalendarIcon } from 'iconoir-react'
import moment, { Moment } from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import ClickAwayListener from 'react-click-away-listener'
import { ChevronToggle } from '../ChevronToggle'
import CalendarRangePicker from './CalendarRangePicker'
import Combo from 'app/components/ui/combo/combo'
import { useSettings } from 'app/hooks/useSettings'
import { useHandleSearchParams } from 'app/utils'
import { OptionType } from 'types/OptionType'

const SelectorPresetOptions: OptionType[] = [
  { name: `Custom Date`, value: `custom`, id: `custom` },
  { name: `This week`, value: `this_week`, id: `this_week` },
  { name: `Last week`, value: `last_week`, id: `last_week` },
  { name: `This month`, value: `this_month`, id: `this_month` },
  { name: `Last month`, value: `last_month`, id: `last_month` },
  { name: `This year`, value: `this_year`, id: `this_year` },
  { name: `Last year`, value: `last_year`, id: `last_year` },
  { name: `Last 30 days`, value: `last_30_days`, id: `last_30_days` },
  { name: `Last 90 days`, value: `last_90_days`, id: `last_90_days` },
  { name: `Last 6 months`, value: `last_6_months`, id: `last_6_months` },
  { name: `Last 12 months`, value: `last_12_months`, id: `last_12_months` },
]

export type DateRangeOptions =
  | `custom`
  | `this_week`
  | `last_week`
  | `this_month`
  | `last_month`
  | `this_year`
  | `last_year`
  | `last_30_days`
  | `last_90_days`
  | `last_6_months`
  | `last_12_months`

export type DateRangeCompareOptions = `previous_year` | `previous_period` | `goals` | `custom` | `none`

const CompareSelectorPresetOptions: OptionType[] = [
  { name: `Previous year`, value: `previous_year`, id: `previous_year` },
  { name: `Previous period`, value: `previous_period`, id: `previous_period` },
  { name: `Goals`, value: `goals`, id: `goals` },
  { name: `Custom Date`, value: `custom`, id: `custom` },
  { name: `None`, value: `none`, id: `none` },
]

/** Returns true if the given element is inside a Radix UI portal (popover content rendered via Portal). */
export const isInsideRadixPortal = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  return !!(target.closest(`[data-radix-popper-content-wrapper]`) || target.closest(`[data-slot="popover-content"]`))
}

/** Resolves which compare preset to use based on priority: URL param > saved setting > default */
export const resolveComparePreset = (
  urlParam: string | null,
  savedPreset: string | undefined,
  defaultPreset: DateRangeCompareOptions,
  excludedOptions: DateRangeCompareOptions[],
): DateRangeCompareOptions => {
  if (urlParam) return urlParam as DateRangeCompareOptions
  if (savedPreset && savedPreset !== `custom` && !excludedOptions.includes(savedPreset as DateRangeCompareOptions)) {
    return savedPreset as DateRangeCompareOptions
  }
  return defaultPreset
}

type Props = {
  onDateChange?: (
    fromDate: string,
    toDate: string,
    compareFromDate: string,
    compareToDate: string,
    isComparingToGoals: boolean,
    dontCompare: boolean,
  ) => void
  customSelectorPresentClassName?: string
  customLabelClassName?: string
  showOnRight?: boolean
  limitToMonth?: boolean
  defaultSelectorPreset?: DateRangeOptions
  defaultCompareSelectorPreset?: DateRangeCompareOptions
  hideCompare?: boolean
  showDateLabel?: boolean
  allowFutureDates?: boolean
  compareSelectorBlacklist?: DateRangeCompareOptions[]
}

const EMPTY_COMPARE_SELECTOR_BLACKLIST: DateRangeCompareOptions[] = []

export default function MarketingDateRangeSelector({
  onDateChange,
  customSelectorPresentClassName,
  customLabelClassName,
  showOnRight = false,
  limitToMonth = true,
  hideCompare = false,
  defaultSelectorPreset = `this_year`,
  defaultCompareSelectorPreset = `previous_year`,
  showDateLabel = false,
  allowFutureDates = false,
  compareSelectorBlacklist = EMPTY_COMPARE_SELECTOR_BLACKLIST,
}: Props) {
  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()
  // One setting per dashboard, so each page restores the compare status the user last set there.
  const settingsKey = `compare-${pathname}`
  const [savedPreset, setSettings, settingsLoaded] = useSettings(settingsKey)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [fromDate, setFromDate] = useState<Moment>(moment(searchParams.get(`fromDate`)) ?? moment().startOf(`year`))
  const fromDateRef = useRef<Moment | null>(null)
  const [toDate, setToDate] = useState<Moment>(moment(searchParams.get(`toDate`)) ?? moment())
  const [compareFromDate, setCompareFromDate] = useState<Moment>(
    moment(searchParams.get(`compareFromDate`)) ?? moment().subtract(1, `year`).startOf(`year`),
  )
  const [compareToDate, setCompareToDate] = useState<Moment>(
    moment(searchParams.get(`compareToDate`)) ?? moment().subtract(1, `year`).endOf(`year`),
  )
  const [dontCompare, setDontCompare] = useState(false)
  const [openFromToDatePicker, setOpenFromToDatePicker] = useState(false)
  const [openCompareFromToDatePicker, setOpenCompareFromToDatePicker] = useState(false)
  const [selectorPreset, setSelectorPreset] = useState<DateRangeOptions>(
    (searchParams.get(`selectorPreset`) as any) ?? defaultSelectorPreset,
  )
  const [compareSelectorPreset, setCompareSelectorPreset] = useState<string>(
    searchParams.get(`compareSelectorPreset`) ?? defaultCompareSelectorPreset,
  )
  const [isComparingToGoals, setIsComparingToGoals] = useState(searchParams.get(`isComparingToGoals`) === `true`)
  const handleSearchParams = useHandleSearchParams()
  const lastUpdateFromRef = useRef<`main` | `compare` | null>(null)
  const hasAppliedSavedPreset = useRef(false)
  // The compare URL param at first render, before an effect echoes the default back
  // into the URL. The apply effect uses this so the saved setting wins over that echo.
  const initialUrlComparePreset = useRef(searchParams.get(`compareSelectorPreset`))

  // Apply saved compare preset once settings load (if no URL param override)
  useEffect(() => {
    if (!settingsLoaded || hasAppliedSavedPreset.current) return
    hasAppliedSavedPreset.current = true
    const resolved = resolveComparePreset(
      initialUrlComparePreset.current,
      savedPreset as string | undefined,
      defaultCompareSelectorPreset,
      compareSelectorBlacklist,
    )
    if (resolved !== compareSelectorPreset) setCompareSelectorPreset(resolved)
  }, [settingsLoaded])

  // Floating UI for dropdown positioning with proper click-away handling
  const { refs, floatingStyles, context } = useFloating({
    open: isModalOpen,
    onOpenChange: setIsModalOpen,
    placement: showOnRight ? `bottom-start` : `bottom-end`,
    whileElementsMounted: autoUpdate,
    // shift keeps the popover inside the viewport on narrow screens (flip alone
    // only swaps sides, it won't pull an overflowing popover back on-screen).
    middleware: [offset(8), flip({ fallbackAxisSideDirection: `start`, padding: 5 }), shift({ padding: 8 })],
  })
  const click = useClick(context)
  const dismiss = useDismiss(context, {
    outsidePress: (event) => !isInsideRadixPortal(event.target as EventTarget),
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss])

  const updateDatesAndParams = () => {
    if (onDateChange) {
      onDateChange(
        fromDate.format(`YYYY-MM-DD`),
        toDate.format(`YYYY-MM-DD`),
        compareFromDate.format(`YYYY-MM-DD`),
        compareToDate.format(`YYYY-MM-DD`),
        isComparingToGoals,
        dontCompare,
      )
    }
    handleSearchParams({
      fromDate: fromDate.format(`YYYY-MM-DD`),
      toDate: toDate.format(`YYYY-MM-DD`),
      selectorPreset,
      ...(!hideCompare && {
        compareFromDate: compareFromDate.format(`YYYY-MM-DD`),
        compareToDate: compareToDate.format(`YYYY-MM-DD`),
        isComparingToGoals: isComparingToGoals.toString(),
        compareSelectorPreset,
      }),
    })
  }

  useEffect(() => {
    switch (selectorPreset) {
      case `this_week`:
        setFromDate(moment().startOf(`week`))
        setToDate(moment().endOf(`week`))
        break
      case `last_week`:
        setFromDate(moment().subtract(1, `week`).startOf(`week`))
        setToDate(moment().subtract(1, `week`).endOf(`week`))
        break
      case `this_month`:
        setFromDate(moment().startOf(`month`))
        setToDate(moment().endOf(`month`))
        break
      case `last_month`:
        setFromDate(moment().subtract(1, `month`).startOf(`month`))
        setToDate(moment().subtract(1, `month`).endOf(`month`))
        break
      case `this_year`:
        setFromDate(moment().startOf(`year`))
        setToDate(moment().endOf(`month`))
        break
      case `last_year`:
        setFromDate(moment().subtract(1, `year`).startOf(`year`))
        setToDate(moment().subtract(1, `year`).endOf(`year`))
        break
      case `last_30_days`:
        setFromDate(moment().subtract(30, `days`).startOf(`day`))
        setToDate(moment().endOf(`day`))
        break
      case `last_90_days`:
        setFromDate(moment().subtract(90, `days`).startOf(`day`))
        setToDate(moment().endOf(`day`))
        break
      case `last_6_months`:
        setFromDate(moment().subtract(5, `month`).startOf(`month`))
        setToDate(moment().endOf(`month`))
        break
      case `last_12_months`:
        setFromDate(moment().subtract(11, `month`).startOf(`month`))
        setToDate(moment().endOf(`month`))
        break
    }
  }, [selectorPreset])

  useEffect(() => {
    switch (compareSelectorPreset) {
      case `previous_year`:
        setCompareFromDate(fromDate.clone().subtract(1, `year`))
        setCompareToDate(toDate.clone().subtract(1, `year`))
        setIsComparingToGoals(false)
        setDontCompare(false)
        break
      case `previous_period`:
        setCompareFromDate(
          fromDate
            .clone()
            .subtract(toDate.clone().diff(fromDate, `month`) + 1, `month`)
            .startOf(`month`),
        )
        setCompareToDate(fromDate.clone().subtract(1, `month`).endOf(`month`))
        setIsComparingToGoals(false)
        setDontCompare(false)
        break
      case `goals`:
        setCompareFromDate(fromDate)
        setCompareToDate(toDate)
        setIsComparingToGoals(true)
        setDontCompare(false)
        break
      case `custom`:
        setIsComparingToGoals(false)
        setDontCompare(false)
        break
      case `none`:
        setDontCompare(true)
        setCompareFromDate(fromDate)
        setCompareToDate(toDate)
        break
    }
  }, [compareSelectorPreset, fromDate, toDate])

  useEffect(() => {
    if (lastUpdateFromRef.current === `main`) {
      lastUpdateFromRef.current = null
    }

    if (fromDate.isValid() && toDate.isValid() && compareFromDate.isValid() && compareToDate.isValid()) {
      updateDatesAndParams()
      fromDateRef.current = fromDate
    }
  }, [
    // if we track dates and also compare dates when we have both, events will fire twice and can cause oddities.
    // so only track dates when we aren't doing a comparison.
    dontCompare ? fromDate : undefined,
    dontCompare ? toDate : undefined,
    compareFromDate,
    compareToDate,
    isComparingToGoals,
  ])

  // Edge case: User partially updates the main date selection, then updates the compare date selection (useEffect 1 triggers),
  // then user completes update on main date selection but useEffect 1 doesn't trigger because fromDate/toDate aren't in deps when !dontCompare
  useEffect(() => {
    if (
      lastUpdateFromRef.current === `main` &&
      !dontCompare &&
      fromDate.isValid() &&
      toDate.isValid() &&
      compareFromDate.isValid() &&
      compareToDate.isValid()
    ) {
      updateDatesAndParams()
      lastUpdateFromRef.current = null
    }
  }, [fromDate, toDate])

  const handleFromToDateChange = (dates: Date[]) => {
    setSelectorPreset(`custom`)
    setFromDate(moment(dates[0]))
    const momentToDate = limitToMonth ? moment(dates[1]).endOf(`month`) : moment(dates[1])
    setToDate(momentToDate)
    lastUpdateFromRef.current = `main`
  }

  const handleCompareFromToDateChange = (dates: Date[]) => {
    setCompareSelectorPreset(`custom`)
    setCompareFromDate(moment.utc(moment(dates[0]).format(limitToMonth ? `YYYY-MM` : `YYYY-MM-DD`)))
    setCompareToDate(moment.utc(moment(dates[1]).endOf(`month`).format(`YYYY-MM-DD`)))
    lastUpdateFromRef.current = `compare`
  }

  const dateRange = useMemo(() => `${fromDate.format(`MMM D, YY`)} - ${toDate.format(`MMM D, YY`)}`, [fromDate, toDate])

  const getDateRange = (): string => {
    const selector = SelectorPresetOptions.find((option) => option.value === selectorPreset)
    return selector?.name === `Custom Date` ? dateRange : selector?.name || ``
  }

  return (
    <>
      <div
        ref={refs.setReference}
        className={classNames(`btn btn-sm btn-tertiary w-auto pr-3.5 md:w-52`, isModalOpen && `bg-surface-muted`)}
        {...getReferenceProps()}
      >
        <div className={classNames(`flex w-full cursor-pointer items-center justify-between gap-x-4`)}>
          <div className="flex items-center whitespace-nowrap">
            <CalendarIcon className="mb-0.5 h-4 w-4" />
            <div className={classNames(customLabelClassName ?? `ml-2 text-xs text-default`)}>
              {showDateLabel && (
                <>
                  <span className="table-filter-options">Date</span>&nbsp;
                </>
              )}
              <span className={classNames(customSelectorPresentClassName)}>{getDateRange()}</span>
            </div>
          </div>
          <ChevronToggle open={isModalOpen} className="pr-0" />
        </div>
      </div>
      {isModalOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false} closeOnFocusOut={false}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className="z-40 flex w-[calc(100vw-1rem)] max-w-[350px] flex-wrap items-center divide-x divide-default rounded-xl border border-default bg-surface-raised shadow-elevation-4 sm:w-[350px]"
              {...getFloatingProps()}
            >
              <div className="w-full p-4">
                <div>
                  <p className="text-body-sm text-strong">Date range</p>
                  <Combo
                    inputClassName="mt-1 !h-9 cursor-pointer rounded-lg border !border-strong !px-3 text-body-sm text-strong shadow-elevation-1 focus:!border-blue-600 focus:!outline-none focus:!ring-1 focus:!ring-strong"
                    options={SelectorPresetOptions}
                    value={SelectorPresetOptions.find((option) => option.value === selectorPreset)}
                    onChange={(option) => setSelectorPreset(option.value as DateRangeOptions)}
                  />
                  <div className="mt-4 flex items-center justify-between">
                    <ClickAwayListener onClickAway={() => setOpenFromToDatePicker(false)}>
                      <div className="group relative w-full">
                        <div
                          onClick={() => setOpenFromToDatePicker((prev) => !prev)}
                          className="flex cursor-pointer items-center rounded-lg border border-strong px-4 py-2.5 text-body-xs text-strong focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-strong"
                        >
                          <CalendarIcon className="h-4 w-4" />
                          <div className="flex grow items-center justify-evenly">
                            <span>{fromDate.format(limitToMonth ? `MMMM YYYY` : `MM/DD/YY`)}</span>
                            <span>-</span>
                            <span>{toDate.format(limitToMonth ? `MMMM YYYY` : `MM/DD/YY`)}</span>
                          </div>
                        </div>
                        <div
                          className={classNames(
                            `absolute inset-y-0 top-11 z-10 h-fit w-80 rounded-lg border border-default bg-surface-raised p-2 shadow-elevation-2`,
                            openFromToDatePicker ? `block` : `hidden`,
                          )}
                        >
                          <CalendarRangePicker
                            limitToMonth={limitToMonth}
                            startDate={fromDate}
                            endDate={toDate}
                            onDateChange={handleFromToDateChange}
                            allowFutureDates={allowFutureDates}
                          />
                        </div>
                      </div>
                    </ClickAwayListener>
                  </div>
                </div>
                {!hideCompare && (
                  <div className=" mt-8">
                    <p className="text-body-sm text-strong">Compare to</p>
                    <Combo
                      inputClassName="mt-1 !h-9 cursor-pointer rounded-lg border !border-strong !px-3 text-body-sm text-strong shadow-elevation-1 focus:!border-blue-600 focus:!outline-none focus:!ring-1 focus:!ring-strong"
                      options={CompareSelectorPresetOptions.filter(
                        (option) => !compareSelectorBlacklist.includes(option.value as DateRangeCompareOptions),
                      )}
                      value={CompareSelectorPresetOptions.find((option) => option.value === compareSelectorPreset)}
                      onChange={(option) => {
                        setCompareSelectorPreset(option.value as string)
                        setSettings(settingsKey, option.value)
                      }}
                    />
                    <div className="mt-4 flex justify-between">
                      <ClickAwayListener onClickAway={() => setOpenCompareFromToDatePicker(false)}>
                        <div className="group relative w-full">
                          <div
                            onClick={(e) =>
                              dontCompare ? e.stopPropagation() : setOpenCompareFromToDatePicker((prev) => !prev)
                            }
                            className={classNames(
                              `flex cursor-pointer rounded-lg border border-strong px-4 py-2.5 text-body-xs text-strong focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-strong`,
                              dontCompare ? `pointer-events-none cursor-not-allowed opacity-50` : ``,
                            )}
                          >
                            <CalendarIcon className=" h-4 w-4" />
                            <div className="flex grow justify-evenly">
                              <span>{compareFromDate.format(limitToMonth ? `MMMM YYYY` : `MM/DD/YY`)}</span>
                              <span>-</span>
                              <span>{compareToDate.format(limitToMonth ? `MMMM YYYY` : `MM/DD/YY`)}</span>
                            </div>
                          </div>
                          <div
                            className={classNames(
                              `absolute inset-y-0 top-11 z-10 h-fit w-80 rounded-lg border border-default bg-surface-raised p-2 shadow-elevation-2`,
                              openCompareFromToDatePicker ? `block` : `hidden`,
                            )}
                          >
                            <CalendarRangePicker
                              limitToMonth={limitToMonth}
                              startDate={compareFromDate}
                              endDate={compareToDate}
                              onDateChange={handleCompareFromToDateChange}
                              allowFutureDates={allowFutureDates}
                            />
                          </div>
                        </div>
                      </ClickAwayListener>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  )
}
